import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, PageHeader, Modal, Button, Field, Input, Select } from "@/components/ui-kit";
import { Boxes, Plus, Trash2, Filter, X, Pencil, AlertTriangle } from "lucide-react";
import { formatNumber, parseQty } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";

// Iter v34aq (#2) — Preset material umum di industri batching plant. Admin
// bisa pilih preset (BJ terisi otomatis) atau "custom" untuk isi manual.
// Sumber angka: rata-rata industri Indonesia (bisa disesuaikan admin).
const MATERIAL_PRESETS = [
  { key: "semen",   label: "Semen (PCC/OPC)",    mode: "m3_to_kg",    bj: 1440, note: "1 m³ ≈ 1440 kg" },
  { key: "pasir",   label: "Pasir Beton",         mode: "m3_to_kg",    bj: 1600, note: "1 m³ ≈ 1600 kg" },
  { key: "kerikil", label: "Kerikil / Split",     mode: "m3_to_kg",    bj: 1500, note: "1 m³ ≈ 1500 kg" },
  { key: "abu",     label: "Abu Batu",            mode: "m3_to_kg",    bj: 1700, note: "1 m³ ≈ 1700 kg" },
  { key: "air",     label: "Air",                 mode: "m3_to_liter", bj: 1000, note: "1 m³ = 1000 L (density 1)" },
  { key: "solar",   label: "Solar / BBM",         mode: "m3_to_liter", bj: 850,  note: "1 m³ = 1000 L · 0,85 kg/L" },
  { key: "aditif",  label: "Aditif / Superplasticizer", mode: "direct_kg", bj: 0, note: "Langsung dalam kg" },
  { key: "custom",  label: "Custom / Lainnya",    mode: "m3_to_kg",    bj: 1500, note: "" },
];

const CONVERSION_MODES = [
  { value: "m3_to_kg",    label: "Kubik → Kilogram",  hint: "Input m³, konversi ke kg via berat jenis. Umum: semen, pasir, split." },
  { value: "m3_to_liter", label: "Kubik → Liter",     hint: "Input m³, konversi ke liter (1 m³ = 1000 L). Untuk cairan: air, solar." },
  { value: "direct_kg",   label: "Langsung Kilogram", hint: "Selalu diinput kg, tidak perlu konversi. Untuk: aditif, additive kimia." },
];

const BLANK = {
  name: "",
  bj_kg_per_m3: 1500,
  min_stock_kg: 0,
  note: "",
  conversion_mode: "m3_to_kg",
  preset_key: "",
};

export default function BatchingStock() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  // Iter v34br — Edit mode: null = create, id = edit existing.
  const [editId, setEditId] = useState(null);
  // Iter v34bm (Task #2) — Filter rentang tanggal untuk in_kg/out_kg.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Iter v34bs (Task 1) — Modal pelaporan susut/hilang.
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ material_id: "", material_name: "", qty_value: 0, qty_unit: "kg", reason: "susut", note: "", date: new Date().toISOString().slice(0, 10) });
  const [adjSaving, setAdjSaving] = useState(false);
  // Iter v34cz (#5) — Pengalih satuan tampilan kolom Masuk/Keluar/Saldo: kg ⇄ m³.
  const [unitView, setUnitView] = useState("kg");
  const conv = (m, kg) => {
    const val = Number(kg) || 0;
    const bj = Number(m.bj_kg_per_m3) || 0;
    if (unitView === "kg" || m.conversion_mode === "direct_kg" || !bj) return { v: val, u: "kg", d: 2 };
    if (m.conversion_mode === "m3_to_liter") return { v: (val / bj) * 1000, u: "L", d: 2 };
    return { v: val / bj, u: "m³", d: 3 };
  };
  const Qty = ({ m, kg, className = "" }) => {
    const { v, u, d } = conv(m, kg);
    return <span className={className}>{formatNumber(v, d)} <span className="text-[10px] text-stone-400">{u}</span></span>;
  };

  const load = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return api.get("/batching/stock-summary", { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (key) => {
    const p = MATERIAL_PRESETS.find((x) => x.key === key);
    if (!p) return;
    setForm((f) => ({
      ...f,
      preset_key: key,
      conversion_mode: p.mode,
      bj_kg_per_m3: p.bj || f.bj_kg_per_m3,
      // Auto-fill name only if user hasn't typed anything yet OR still matches previous preset.
      name: (!f.name || MATERIAL_PRESETS.some((x) => x.label === f.name)) && key !== "custom" ? p.label : f.name,
      note: p.note || f.note,
    }));
  };

  const addMaterial = async () => {
    if (!form.name) { toast.error("Nama material wajib diisi"); return; }
    try {
      const payload = {
        ...form,
        bj_kg_per_m3: parseQty(form.bj_kg_per_m3),
        min_stock_kg: parseQty(form.min_stock_kg),
      };
      if (editId) {
        // Iter v34br — Edit existing material.
        await api.patch(`/batching/materials/${editId}`, payload);
        toast.success("Material diperbarui");
      } else {
        await api.post("/batching/materials", payload);
        toast.success("Material ditambahkan");
      }
      setOpen(false);
      setEditId(null);
      setForm({ ...BLANK });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Iter v34br — Open edit modal for existing material.
  const editMaterial = (m) => {
    setEditId(m.id);
    setForm({
      name: m.name || "",
      bj_kg_per_m3: m.bj_kg_per_m3 || "",
      conversion_mode: m.conversion_mode || "m3_to_kg",
      note: m.note || "",
      min_stock_kg: m.min_stock_kg || "",
      preset_key: "",
    });
    setOpen(true);
  };

  // Iter v34bs (Task 1) — Buka Modal pelaporan susut/hilang.
  const openAdjustment = (m) => {
    setAdjForm({ material_id: m.id, material_name: m.name, qty_value: 0, qty_unit: "kg", reason: "susut", note: "", date: new Date().toISOString().slice(0, 10) });
    setAdjOpen(true);
  };
  const submitAdjustment = async () => {
    const qty = parseQty(adjForm.qty_value);
    if (qty <= 0) { toast.error("Jumlah harus > 0"); return; }
    if (!adjForm.reason) { toast.error("Alasan wajib diisi"); return; }
    setAdjSaving(true);
    try {
      await api.post("/batching/stock-entries", {
        material_id: adjForm.material_id,
        type: "adjustment",
        qty_value: qty,
        qty_unit: adjForm.qty_unit,
        date: adjForm.date,
        ref: `PENYESUAIAN-${adjForm.reason.toUpperCase()}`,
        note: adjForm.note,
        adjustment_reason: adjForm.reason,
      });
      toast.success(`Penyesuaian stok "${adjForm.reason}" berhasil dicatat`);
      setAdjOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setAdjSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus material ini? Semua riwayat stock entry tetap tersimpan.")) return;
    try { await api.delete(`/batching/materials/${id}`); load(); }
    catch (e) { toast.error("Gagal hapus"); }
  };

  const activeMode = CONVERSION_MODES.find((m) => m.value === form.conversion_mode) || CONVERSION_MODES[0];
  const isDirectKg = form.conversion_mode === "direct_kg";
  const isLiter = form.conversion_mode === "m3_to_liter";

  return (
    <div className="space-y-4">
      {/* Iter v34bp — Header dirapihkan: description dipadatkan, filter tanggal terintegrasi di baris di bawah judul. */}
      <PageHeader
        overline="Batching Plant"
        title="Stok Material"
        description={unitView === "kg" ? "Saldo material · Masuk − Keluar (kg)." : "Saldo material · Masuk − Keluar (m³ / L, hasil konversi BJ)."}
        icon={Boxes}
        action={<Button onClick={() => { setForm({ ...BLANK }); setOpen(true); }} data-testid="batching-material-add"><Plus className="h-4 w-4" /> Material Baru</Button>}
      />
      {/* Filter rentang tanggal — kompak, in-line, langsung di bawah header agar terlihat jelas. */}
      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-stone-600 mr-2">
            <Filter className="h-3.5 w-3.5" /> Rentang Transaksi
          </div>
          <Field label="Dari">
            <DatePicker value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="batching-stock-filter-from" />
          </Field>
          <Field label="Sampai">
            <DatePicker value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="batching-stock-filter-to" />
          </Field>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="batching-stock-filter-reset">
              <X className="h-3.5 w-3.5" /> Reset
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {/* Iter v34cz (#5) — Toggle satuan tampilan */}
            <div className="inline-flex border border-stone-300 overflow-hidden" data-testid="batching-stock-unit-toggle">
              {["kg", "m3"].map((u) => (
                <button key={u} type="button" onClick={() => setUnitView(u)}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${unitView === u ? "bg-stone-800 text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
                  data-testid={`batching-stock-unit-${u}`}>
                  {u === "kg" ? "kg" : "m³ / L"}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-stone-500 leading-tight">
              {dateFrom || dateTo
                ? "Masuk/Keluar difilter · Saldo tetap akumulatif"
                : "Semua rentang · kolom Masuk & Keluar akumulatif"}
            </div>
          </div>
        </div>
      </Card>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
            <tr><th className="text-left py-2 px-3">Material</th><th className="text-right py-2 px-3">BJ (kg/m³)</th><th className="text-right py-2 px-3">Min Stok</th><th className="text-right py-2 px-3">Masuk ({unitView === "kg" ? "kg" : "m³/L"})</th><th className="text-right py-2 px-3">Keluar ({unitView === "kg" ? "kg" : "m³/L"})</th><th className="text-right py-2 px-3">Saldo ({unitView === "kg" ? "kg" : "m³/L"})</th>{unitView === "kg" && <th className="text-right py-2 px-3">Saldo (m³)</th>}<th className="text-right py-2 px-3 w-20">Aksi</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-400 py-8 text-xs">Belum ada material. Klik tombol di kanan atas untuk mulai.</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} className={`border-b border-stone-100 hover:bg-stone-50/40 ${m.is_low_stock ? "bg-red-50/40" : ""}`} data-testid={`batching-stock-row-${m.id}`}>
                <td className="py-2 px-3">
                  <span className="font-semibold">{m.name}</span>
                  {m.conversion_mode && m.conversion_mode !== "m3_to_kg" && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 uppercase font-semibold" data-testid={`conv-mode-${m.id}`}>
                      {m.conversion_mode === "m3_to_liter" ? "m³→L" : "kg"}
                    </span>
                  )}
                  {m.note && <span className="text-[10px] text-stone-500 ml-2">({m.note})</span>}
                  {m.is_low_stock && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold uppercase" data-testid={`stock-low-badge-${m.id}`}>⚠ Stok Minim</span>}
                </td>
                <td className="py-2 px-3 text-right font-mono">{formatNumber(m.bj_kg_per_m3)}</td>
                <td className="py-2 px-3 text-right font-mono text-stone-500">{m.min_stock_kg ? formatNumber(m.min_stock_kg) : "—"}</td>
                <td className="py-2 px-3 text-right font-mono text-emerald-700" data-testid={`stock-in-${m.id}`}><Qty m={m} kg={m.in_kg} /></td>
                <td className="py-2 px-3 text-right font-mono text-red-700" data-testid={`stock-out-${m.id}`}><Qty m={m} kg={m.out_kg} /></td>
                <td className={`py-2 px-3 text-right font-mono font-bold ${m.is_low_stock ? "text-red-700" : ""}`} data-testid={`stock-balance-${m.id}`}><Qty m={m} kg={m.balance_kg} /></td>
                {unitView === "kg" && <td className="py-2 px-3 text-right font-mono">{formatNumber(m.balance_m3, 3)}</td>}
                <td className="py-2 px-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => openAdjustment(m)} className="text-stone-500 hover:text-amber-700" title="Lapor Susut / Hilang" data-testid={`batching-material-adjust-${m.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => editMaterial(m)} className="text-stone-500 hover:text-blue-700" title="Edit material" data-testid={`batching-material-edit-${m.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => del(m.id)} className="text-stone-500 hover:text-red-700" title="Hapus material" data-testid={`batching-material-delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Iter v34bs (Task 1) — Modal Lapor Susut / Hilang / Rusak */}
      <Modal open={adjOpen} onClose={() => setAdjOpen(false)} title={`Lapor Penyesuaian Stok · ${adjForm.material_name}`} size="md">
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 inline mr-1"/>
            Gunakan form ini untuk melaporkan <b>susut / hilang / rusak</b> agar saldo stok di sistem sesuai dengan kondisi di lapangan. Entry akan disimpan sebagai <b>Penyesuaian</b> (mengurangi saldo).
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Alasan" hint="Kategori penyesuaian">
              <Select value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} data-testid="adj-reason">
                <option value="susut">Susut / Penguapan</option>
                <option value="hilang">Hilang</option>
                <option value="rusak">Rusak / Kontaminasi</option>
                <option value="koreksi">Koreksi Stok Opname</option>
                <option value="lainnya">Lainnya</option>
              </Select>
            </Field>
            <Field label="Tanggal"><DatePicker value={adjForm.date} onChange={(e) => setAdjForm({ ...adjForm, date: e.target.value })}/></Field>
            <Field label="Jumlah">
              <Input type="number" step="0.01" className="text-right font-mono-num" value={adjForm.qty_value} onChange={(e) => setAdjForm({ ...adjForm, qty_value: e.target.value })} data-testid="adj-qty"/>
            </Field>
            <Field label="Satuan">
              <Select value={adjForm.qty_unit} onChange={(e) => setAdjForm({ ...adjForm, qty_unit: e.target.value })} data-testid="adj-unit">
                <option value="kg">kg</option>
                <option value="m3">m³</option>
              </Select>
            </Field>
          </div>
          <Field label="Catatan" hint="Detail kejadian (opsional)">
            <Input value={adjForm.note} onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })} placeholder="mis: Susut penguapan pasir selama 1 bulan hujan" data-testid="adj-note"/>
          </Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setAdjOpen(false)}>Batal</Button>
            <Button onClick={submitAdjustment} disabled={adjSaving} data-testid="adj-submit">
              <AlertTriangle className="h-3.5 w-3.5"/> {adjSaving ? "Menyimpan..." : "Simpan Penyesuaian"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); setForm({ ...BLANK }); }} title={editId ? "Edit Material" : "Material Baru"} size="md">
        <div className="space-y-3">
          {/* Preset selector — auto-fills mode + BJ + name */}
          <Field label="Pilih Preset (opsional)" hint="Preset akan mengisi mode konversi & berat jenis. Pilih 'Custom' untuk isi manual.">
            <select
              value={form.preset_key}
              onChange={(e) => applyPreset(e.target.value)}
              className="w-full border border-stone-300 px-3 py-2 text-sm bg-white focus:border-terra-500 focus:ring-1 focus:ring-terra-500 outline-none"
              data-testid="batching-material-preset"
            >
              <option value="">— Pilih preset —</option>
              {MATERIAL_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}{p.bj ? ` · ${p.bj} kg/m³` : ""}</option>
              ))}
            </select>
          </Field>

          <Field label="Nama Material" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Semen / Pasir / Split 1-2 / Aditif" data-testid="batching-material-name" />
          </Field>

          <Field label="Mode Konversi Berat Jenis" required hint={activeMode.hint}>
            <select
              value={form.conversion_mode}
              onChange={(e) => setForm({ ...form, conversion_mode: e.target.value })}
              className="w-full border border-stone-300 px-3 py-2 text-sm bg-white focus:border-terra-500 focus:ring-1 focus:ring-terra-500 outline-none"
              data-testid="batching-material-conv-mode"
            >
              {CONVERSION_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>

          {!isDirectKg && (
            <Field
              label={isLiter ? "Berat Jenis (kg per m³) — 1000 untuk air, 850 untuk solar" : "Berat Jenis (kg per m³)"}
              required
              hint={isLiter ? "Density untuk konversi liter → kg. Default 1000 (air murni)." : "Untuk konversi m³ → kg. Rekomendasi: semen 1440, pasir 1600, split 1500."}
            >
              <Input className="font-mono-num" value={form.bj_kg_per_m3} onChange={(e) => setForm({ ...form, bj_kg_per_m3: e.target.value })} placeholder="1500" data-testid="batching-material-bj" />
            </Field>
          )}
          {isDirectKg && (
            <div className="bg-blue-50 border border-blue-200 p-2.5 text-xs text-blue-800">
              Mode <b>Langsung Kilogram</b>: input stok akan diperlakukan sebagai kg apapun satuan yang dipilih. Cocok untuk aditif kimia yang biasanya per-drum/kantong.
            </div>
          )}

          <Field label="Min Stok (kg)" hint="Threshold safety stock. Jika saldo < ini, muncul Alert Stok Minim. 0 = nonaktif.">
            <Input className="font-mono-num" value={form.min_stock_kg} onChange={(e) => setForm({ ...form, min_stock_kg: e.target.value })} placeholder="0" data-testid="batching-material-min-stock" />
          </Field>

          <Field label="Catatan"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="contoh: Semen Tiga Roda PCC" data-testid="batching-material-note" /></Field>

          <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={addMaterial} data-testid="batching-material-save">Simpan</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
